import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { settingsAPI, uploadAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Save, Palette, Bell, BarChart3, Upload, Image, FileCode, Trash2, ExternalLink } from 'lucide-react';

export const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [verificationFiles, setVerificationFiles] = useState([]);
  
  const faviconInputRef = useRef(null);
  const ogImageInputRef = useRef(null);
  const verificationInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    fetchVerificationFiles();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationFiles = async () => {
    try {
      const response = await uploadAPI.getVerificationFiles();
      setVerificationFiles(response.data);
    } catch (e) {
      console.error('Failed to load verification files');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast.success('Settings saved successfully!');
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(prev => ({ ...prev, favicon: true }));
    try {
      const response = await uploadAPI.favicon(file);
      updateField('favicon_url', response.data.url);
      toast.success('Favicon uploaded!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload favicon');
    } finally {
      setUploading(prev => ({ ...prev, favicon: false }));
      if (faviconInputRef.current) faviconInputRef.current.value = '';
    }
  };

  const handleOgImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(prev => ({ ...prev, ogImage: true }));
    try {
      const response = await uploadAPI.ogImage(file);
      updateField('og_image', response.data.url);
      toast.success('OG Image uploaded!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload OG image');
    } finally {
      setUploading(prev => ({ ...prev, ogImage: false }));
      if (ogImageInputRef.current) ogImageInputRef.current.value = '';
    }
  };

  const handleVerificationUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(prev => ({ ...prev, verification: true }));
    try {
      await uploadAPI.verification(file);
      toast.success('Verification file uploaded!');
      fetchVerificationFiles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(prev => ({ ...prev, verification: false }));
      if (verificationInputRef.current) verificationInputRef.current.value = '';
    }
  };

  const handleDeleteVerification = async (filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;
    try {
      await uploadAPI.deleteVerificationFile(filename);
      toast.success('File deleted!');
      fetchVerificationFiles();
    } catch (e) {
      toast.error('Failed to delete file');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-blue-600" />
      </div>
    );
  }

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-8" data-testid="admin-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Site Settings</h1>
          <p className="mt-2 text-slate-600">Configure your sales page appearance and content</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Branding */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Branding
            </CardTitle>
            <CardDescription>Site name and colors</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="site_name">Site Name</Label>
              <Input
                id="site_name"
                value={settings?.site_name || ''}
                onChange={(e) => updateField('site_name', e.target.value)}
                data-testid="input-site-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="site_tagline">Tagline</Label>
              <Input
                id="site_tagline"
                value={settings?.site_tagline || ''}
                onChange={(e) => updateField('site_tagline', e.target.value)}
                data-testid="input-tagline"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primary_color">Primary</Label>
                <div className="flex gap-2">
                  <Input
                    id="primary_color"
                    type="color"
                    value={settings?.primary_color || '#1e3a8a'}
                    onChange={(e) => updateField('primary_color', e.target.value)}
                    className="w-12 h-10 p-1"
                  />
                  <Input
                    value={settings?.primary_color || ''}
                    onChange={(e) => updateField('primary_color', e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="secondary_color">Secondary</Label>
                <div className="flex gap-2">
                  <Input
                    id="secondary_color"
                    type="color"
                    value={settings?.secondary_color || '#15803d'}
                    onChange={(e) => updateField('secondary_color', e.target.value)}
                    className="w-12 h-10 p-1"
                  />
                  <Input
                    value={settings?.secondary_color || ''}
                    onChange={(e) => updateField('secondary_color', e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="accent_color">Accent</Label>
                <div className="flex gap-2">
                  <Input
                    id="accent_color"
                    type="color"
                    value={settings?.accent_color || '#dc2626'}
                    onChange={(e) => updateField('accent_color', e.target.value)}
                    className="w-12 h-10 p-1"
                  />
                  <Input
                    value={settings?.accent_color || ''}
                    onChange={(e) => updateField('accent_color', e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Promo & Notice Bars */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Promo & Notice Bars
            </CardTitle>
            <CardDescription>Top bar messages and stock count</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="promo_bar_enabled">Enable Promo Bar</Label>
              <Switch
                id="promo_bar_enabled"
                checked={settings?.promo_bar_enabled || false}
                onCheckedChange={(checked) => updateField('promo_bar_enabled', checked)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="promo_bar_text">Promo Bar Text</Label>
              <Textarea
                id="promo_bar_text"
                value={settings?.promo_bar_text || ''}
                onChange={(e) => updateField('promo_bar_text', e.target.value)}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="stock_count">Stock Count</Label>
              <Input
                id="stock_count"
                type="number"
                value={settings?.stock_count || 0}
                onChange={(e) => updateField('stock_count', parseInt(e.target.value))}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="notice_bar_enabled">Enable Notice Bar</Label>
              <Switch
                id="notice_bar_enabled"
                checked={settings?.notice_bar_enabled || false}
                onCheckedChange={(checked) => updateField('notice_bar_enabled', checked)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notice_bar_text">Notice Bar Text</Label>
              <Textarea
                id="notice_bar_text"
                value={settings?.notice_bar_text || ''}
                onChange={(e) => updateField('notice_bar_text', e.target.value)}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deal_timer_minutes">Deal Timer (minutes)</Label>
              <Input
                id="deal_timer_minutes"
                type="number"
                value={settings?.deal_timer_minutes || 10}
                onChange={(e) => updateField('deal_timer_minutes', parseInt(e.target.value))}
              />
            </div>
          </CardContent>
        </Card>

        {/* Favicon & OG Image Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              Site Images
            </CardTitle>
            <CardDescription>Favicon and social sharing image</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Favicon */}
            <div className="space-y-3">
              <Label>Favicon</Label>
              <div className="flex items-center gap-4">
                {settings?.favicon_url && (
                  <div className="w-12 h-12 border rounded-lg flex items-center justify-center bg-slate-50">
                    <img 
                      src={`${API_URL}${settings.favicon_url}`} 
                      alt="Favicon" 
                      className="max-w-full max-h-full"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <Input
                    ref={faviconInputRef}
                    type="file"
                    accept=".ico,.png,.svg"
                    onChange={handleFaviconUpload}
                    className="hidden"
                    id="favicon-upload"
                  />
                  <Button
                    variant="outline"
                    onClick={() => faviconInputRef.current?.click()}
                    disabled={uploading.favicon}
                    className="w-full"
                    data-testid="upload-favicon-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading.favicon ? 'Uploading...' : 'Upload Favicon'}
                  </Button>
                  <p className="mt-1 text-xs text-slate-500">Supported: .ico, .png, .svg</p>
                </div>
              </div>
            </div>

            {/* OG Image */}
            <div className="space-y-3">
              <Label>OG Image (Social Sharing)</Label>
              {settings?.og_image && (
                <div className="rounded-lg border overflow-hidden bg-slate-50">
                  <img 
                    src={`${API_URL}${settings.og_image}`} 
                    alt="OG" 
                    className="w-full h-32 object-cover"
                  />
                </div>
              )}
              <div>
                <Input
                  ref={ogImageInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleOgImageUpload}
                  className="hidden"
                  id="og-upload"
                />
                <Button
                  variant="outline"
                  onClick={() => ogImageInputRef.current?.click()}
                  disabled={uploading.ogImage}
                  className="w-full"
                  data-testid="upload-og-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading.ogImage ? 'Uploading...' : 'Upload OG Image'}
                </Button>
                <p className="mt-1 text-xs text-slate-500">Recommended: 1200x630px, .jpg or .png</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Verification Files */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCode className="w-5 h-5" />
              Verification Files
            </CardTitle>
            <CardDescription>Google Search Console, Bing, sitemap, robots.txt</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Input
                ref={verificationInputRef}
                type="file"
                accept=".html,.xml,.txt"
                onChange={handleVerificationUpload}
                className="hidden"
                id="verification-upload"
              />
              <Button
                variant="outline"
                onClick={() => verificationInputRef.current?.click()}
                disabled={uploading.verification}
                className="w-full"
                data-testid="upload-verification-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading.verification ? 'Uploading...' : 'Upload Verification File'}
              </Button>
              <p className="mt-1 text-xs text-slate-500">
                e.g., google1234abcd.html, sitemap.xml, robots.txt
              </p>
            </div>

            {verificationFiles.length > 0 && (
              <div className="space-y-2 mt-4">
                <Label>Uploaded Files</Label>
                <div className="space-y-2">
                  {verificationFiles.map((file) => (
                    <div 
                      key={file.filename}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileCode className="w-4 h-4 text-slate-500 flex-shrink-0" />
                        <span className="text-sm font-medium truncate">{file.filename}</span>
                        <span className="text-xs text-slate-500">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <a
                          href={`${API_URL}${file.path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 text-slate-500 hover:text-slate-700"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleDeleteVerification(file.filename)}
                          className="p-1 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Analytics */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Analytics Code
            </CardTitle>
            <CardDescription>Google Analytics, Facebook Pixel, etc.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="analytics_code">Analytics Code (JavaScript)</Label>
              <Textarea
                id="analytics_code"
                value={settings?.analytics_code || ''}
                onChange={(e) => updateField('analytics_code', e.target.value)}
                rows={8}
                placeholder="Paste your Google Analytics or other tracking code here..."
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                This code will be injected into the page head.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSettings;
